/**
 * RFC-1146 — download + fullscreen overlay for an image variant tile.
 *
 * The image Variants tab previously had NO overlay buttons at all (only a
 * bottom-left version pill), unlike the video tab whose VideoPreviewPlayer
 * already carries expand/download. This adds the missing pair so both media
 * types expose the same controls, in the same TOP-RIGHT corner.
 *
 * This is THE overlay cluster for an image variant tile — the mirror of
 * VideoPreviewPlayer's single top-right cluster. Order: [expand, download,
 * ...trailingControls], so the variant star sits rightmost, in the same slot
 * as on a video tile.
 *
 * Download navigation is owned by the node-level canonical download handler;
 * this component only isolates the nested button interaction from tile
 * selection.
 */

import { memo, useCallback, type ReactNode } from 'react'
import { Download, Maximize2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface VariantImageOverlayButtonsProps {
  imagePath: string | undefined
  /** Opens the fullscreen lightbox for this image. */
  onExpand: (imagePath: string) => void
  /** Starts the canonical browser-managed attachment download. */
  onDownload: () => void | Promise<void>
  /** Variant index, for stable test ids. */
  variantIndex: number
  /**
   * Controls after download (the variant star). They do NOT inherit the
   * hover gating — the child owns its own visibility, because an archived
   * tile must show its hollow star without hovering.
   */
  trailingControls?: ReactNode
}

const VariantImageOverlayButtons = memo(function VariantImageOverlayButtons({
  imagePath,
  onExpand,
  onDownload,
  variantIndex,
  trailingControls,
}: VariantImageOverlayButtonsProps) {
  const { t } = useTranslation('canvas')

  const handleDownload = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      // Do not let the click bubble to the tile, which would change the picked
      // variant as a side effect of downloading.
      event.stopPropagation()
      event.preventDefault()
      void onDownload()
    },
    [onDownload],
  )

  const handleExpand = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      event.preventDefault()
      if (imagePath) onExpand(imagePath)
    },
    [imagePath, onExpand],
  )

  // Plain template literals (not cn/twMerge) — see VariantMarkerButton.
  // Fixed w-7 h-7 with no `1 / zoom`: the Variants tab is pinned to scale=1
  // and that size-match is intentional UX (CLAUDE.md → Variants Tab Zoom).
  const buttonClass =
    'w-7 h-7 flex items-center justify-center rounded-md bg-black/60 text-white hover:bg-black/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'

  return (
    <div
      className="absolute top-2 right-2 z-30 flex items-center gap-1"
      data-testid={`variant-image-overlay-buttons-${variantIndex}`}
    >
      {/* No image yet (placeholder tile): only the star, disabled. */}
      {imagePath && (
        // `group-focus-within` matters: opacity-0 does NOT remove these
        // buttons from the tab order, so without it a keyboard user focuses
        // invisible controls.
        <div
          className="flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150"
          data-testid={`variant-image-overlay-hover-group-${variantIndex}`}
        >
          <button
            type="button"
            onClick={handleExpand}
            title={t('variantMarker.fullscreen')}
            aria-label={t('variantMarker.fullscreen')}
            data-testid={`variant-image-expand-${variantIndex}`}
            className={buttonClass}
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={handleDownload}
            title={t('variantMarker.download')}
            aria-label={t('variantMarker.download')}
            data-testid={`variant-image-download-${variantIndex}`}
            className={buttonClass}
          >
            <Download className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {trailingControls}
    </div>
  )
})

VariantImageOverlayButtons.displayName = 'VariantImageOverlayButtons'

export { VariantImageOverlayButtons }
